import { ColorEnum } from '@/constants/color'
import type { CatalogCategory } from '@/features/openTabs/components/ProductCatalog'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { Pressable, ScrollView } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface CategoryListProps {
    categories: readonly CatalogCategory[]
    selectedCategoryId: string
    horizontal?: boolean
    onSelect: (categoryId: string) => void
}

const CategoryList: FC<CategoryListProps> = ({
    categories,
    selectedCategoryId,
    horizontal = false,
    onSelect,
}) => {
    const { styles } = useStyles(stylesheet, { horizontal })

    return (
        <ScrollView
            horizontal={horizontal}
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            style={styles.container}
        >
            {categories.map((category) => {
                const selected = category.id === selectedCategoryId

                return (
                    <Pressable
                        accessibilityRole="button"
                        accessibilityState={{ selected }}
                        key={category.id}
                        onPress={() => onSelect(category.id)}
                        style={({ pressed }) => [
                            styles.category,
                            selected && styles.selectedCategory,
                            pressed && styles.pressed,
                        ]}
                    >
                        <Typography.Description
                            font="bold"
                            numberOfLines={1}
                            style={selected && styles.selectedLabel}
                        >
                            {category.name}
                        </Typography.Description>
                    </Pressable>
                )
            })}
        </ScrollView>
    )
}

const stylesheet = createStyleSheet({
    container: {
        flexGrow: 0,
        width: 140,
        variants: {
            horizontal: {
                true: {
                    width: '100%',
                    height: 48,
                    flexShrink: 0,
                },
            },
        },
    },
    content: {
        gap: 8,
        paddingBottom: 16,
        variants: {
            horizontal: {
                true: {
                    flexDirection: 'row',
                    paddingBottom: 0,
                },
            },
        },
    },
    category: {
        height: 48,
        justifyContent: 'center',
        paddingHorizontal: 12,
        borderRadius: 8,
        backgroundColor: ColorEnum.VIOLET_100,
    },
    selectedCategory: {
        backgroundColor: ColorEnum.BRICK_600,
    },
    selectedLabel: {
        color: ColorEnum.WHITE,
    },
    pressed: {
        opacity: 0.72,
    },
})

export default CategoryList
